import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { notify } from '../lib/notify';
import { useNavigate } from 'react-router-dom';
import KanbanBoard from '../components/KanbanBoard';
import { VerseChip } from '../components/ui/VerseChip';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CreatedCell } from '../components/ui/CreatedCell';

type TaleRow = {
  id: string; 
  title: string; 
  verse_ref: string | null; 
  verse_text?: string | null; // Add verse_text for tooltips
  verse_version?: string | null; // Add verse_version for translation info
  status: string;
  created_at: number; 
  updated_at: number;
  createdAtMs?: number;
  createdIso?: string;
  images_total?: number;
  images_ready?: number;
  cultural_context?: string; // Add cultural context
  meta?: any; // Add meta field for additional tale metadata
};



export default function TalesIndex() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'list'|'kanban'>('list'); // default LIST
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('');
  const [items, setItems] = useState<TaleRow[]>([]);
  const [cursor, setCursor] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const [sel, setSel] = useState<Record<string, boolean>>({});
  const selectedIds = useMemo(() => Object.keys(sel).filter(k => sel[k]), [sel]);

  // Helper function to sort tales by creation date (newest first)
  const sortTalesByDate = (tales: TaleRow[]): TaleRow[] => {
    return tales.sort((a: TaleRow, b: TaleRow) => {
      const aTime = a.createdAtMs || a.created_at * 1000 || 0;
      const bTime = b.createdAtMs || b.created_at * 1000 || 0;
      return bTime - aTime; // Descending order (newest first)
    });
  };

  const fetchPage = async (reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api(`/api/tales/list?limit=25&cursor=${reset ? 0 : (cursor ?? 0)}&q=${encodeURIComponent(q)}&status=${status}`);
      // Always sort by creation date (newest first) to ensure consistent ordering
      const sortedItems = sortTalesByDate(r.items || []);
      
      if (reset) setItems(sortedItems);
      else setItems(prev => sortTalesByDate([...prev, ...sortedItems]));
      setCursor(r.nextCursor);
      setCounts(r.counts || {});
    } catch (e: any) {
      notify.error('Failed to load tales', e?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPage(true); /* eslint-disable-next-line */ }, [q, status]);

  const toggleAll = (v: boolean) => {
    const next: Record<string, boolean> = {};
    (items || []).forEach(x => next[x.id] = v);
    setSel(next);
  };

  const rename = async (id: string) => {
    const current = items.find(x => x.id === id);
    const title = prompt('New title', current?.title || 'Untitled');
    if (!title) return;
    await api(`/api/tales/${id}/rename`, { method: 'POST', json: { title } });
    setItems(list => sortTalesByDate(list.map(x => x.id === id ? { ...x, title } : x)));
    notify.success('Renamed', 'Title updated');
  };



  const queueImages = async (ids: string[]) => {
    try {
      if (ids.length === 1) {
        await api(`/api/tales/${ids[0]}/images`, { method: 'POST' });
      } else {
        await api('/api/tales/bulk', { method: 'POST', json: { action: 'queue-images', taleIds: ids } });
      }
      notify.success('Images queued', `${ids.length} tale(s)`);
      setSel({}); 
      fetchPage(true);
    } catch (e: any) {
      notify.error('Queue failed', e?.message);
    }
  };

  const packageNow = async (ids: string[]) => {
    try {
      if (ids.length === 1) {
        await api(`/api/tales/${ids[0]}/package`, { method: 'POST' });
      } else {
        await api('/api/tales/bulk', { method: 'POST', json: { action: 'package', taleIds: ids } });
      }
      notify.success('Packaging started', `${ids.length} tale(s)`);
      setSel({}); 
      fetchPage(true);
    } catch (e: any) {
      notify.error('Package failed', e?.message);
    }
  };

  const softDelete = async (ids: string[]) => {
    const ok = confirm(`Delete ${ids.length} tale(s)? This is a soft delete.`);
    if (!ok) return;
    try {
      await api('/api/tales/bulk', { method: 'POST', json: { action: 'delete', taleIds: ids } });
      notify.success('Deleted', `${ids.length} tale(s)`);
      setSel({});
      fetchPage(true);
    } catch (e: any) {
      notify.error('Delete failed', e?.message);
    }
  };

  const resolveVerses = async (ids: string[]) => {
    try {
      const result = await api('/api/tales/bulk/resolve-verses', { method: 'POST', json: { ids } });
      if (result.updated > 0) {
        notify.success('Verses resolved', `${result.updated} tale(s) updated`);
        setSel({});
        fetchPage(true);
      } else {
        notify.info('No verses resolved', 'No tales had verse references to resolve');
      }
    } catch (e: any) {
      notify.error('Failed to resolve verses', e?.message);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (selectedIds.length === 0) return;
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return; // Don't interfere with input fields
      
      const k = e.key.toLowerCase();
      if (k === 'q') { e.preventDefault(); queueImages(selectedIds); }
      if (k === 'p') { e.preventDefault(); packageNow(selectedIds); }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); softDelete(selectedIds); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds]);

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="text-lg font-semibold">Tales</div>
        <div className="ml-auto flex items-center gap-2">
          <button 
            onClick={() => setTab('list')} 
            className={`px-3 py-1.5 rounded-md text-sm ${tab==='list'?'bg-blue-600 text-white':'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
          >
            List
          </button>
          <button 
            onClick={() => setTab('kanban')} 
            className={`px-3 py-1.5 rounded-md text-sm ${tab==='kanban'?'bg-blue-600 text-white':'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
          >
            Kanban
          </button>
        </div>
      </div>

      {tab === 'list' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input
              className="px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 w-72"
              placeholder="Search title or verse…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <select
              className="px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700"
              value={status} onChange={e => setStatus(e.target.value)}
              aria-label="Filter by status"
              title="Filter by status"
            >
              <option value="">All</option>
              <option value="draft">Draft ({counts['draft'] || 0})</option>
              <option value="images">Images ({counts['images'] || 0})</option>
              <option value="packaged">Packaged ({counts['packaged'] || 0})</option>
              <option value="bound">Bound ({counts['bound'] || 0})</option>
              <option value="scheduled">Scheduled ({counts['scheduled'] || 0})</option>
              <option value="published">Published ({counts['published'] || 0})</option>
              <option value="failed">Failed ({counts['failed'] || 0})</option>
            </select>
            <button 
              onClick={() => fetchPage(true)} 
              className="px-3 py-2 rounded-md bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700"
            >
              Refresh
            </button>
          </div>

          {/* Bulk bar */}
          {selectedIds.length > 0 && (
            <div className="rounded-xl border border-blue-500/30 bg-blue-50 dark:bg-blue-900/20 backdrop-blur p-3 flex items-center gap-3">
              <div className="text-sm"><strong>{selectedIds.length}</strong> selected</div>
              <button onClick={() => queueImages(selectedIds)} className="px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-500 text-white">Queue images (Q)</button>
              <button onClick={() => packageNow(selectedIds)} className="px-3 py-1.5 text-sm rounded-md bg-green-600 hover:bg-green-500 text-white">Package (P)</button>
              <button onClick={() => resolveVerses(selectedIds)} className="px-3 py-1.5 text-sm rounded-md bg-purple-600 hover:bg-purple-500 text-white">Resolve verses</button>
              <button onClick={() => softDelete(selectedIds)} className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-500 text-white">Delete (Del)</button>
              <button onClick={() => setSel({})} className="ml-auto px-3 py-1.5 text-sm rounded-md bg-slate-600 hover:bg-slate-500 text-white">Clear</button>
            </div>
          )}

          {/* LIST TABLE */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/60">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox"
                      onChange={e => toggleAll(e.currentTarget.checked)}
                      checked={items.length>0 && selectedIds.length===items.length}
                      aria-label="Select all tales"
                      title="Select all tales"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Verse</th>
                  <th className="px-3 py-2">Cultural Context</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Images</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => (
                  <tr key={row.id} className="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!!sel[row.id]} onChange={e => setSel(s => ({ ...s, [row.id]: e.currentTarget.checked }))} aria-label={`Select ${row.title || 'Untitled'}`} title={`Select ${row.title || 'Untitled'}`}/>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.title || 'Untitled'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                      <VerseChip refText={row.verse_ref} version={row.verse_version} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const culturalContext = row.cultural_context || (row.meta && typeof row.meta === 'object' ? row.meta.cultural_context : null) || 'universal';
                        const contextMap: Record<string, string> = {
                          'universal': '🌍',
                          'west-african': '🌍',
                          'middle-eastern': '🏜️',
                          'asian': '🏯',
                          'european': '🏰',
                          'latin-american': '🌺'
                        };
                        const emoji = contextMap[culturalContext] || '🌍';
                        const label = culturalContext.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                        return (
                          <span className="text-sm" title={`Cultural Context: ${label}`}>
                            {emoji}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge taleId={row.id} stage={row.status} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(row.images_total || 0) > 0 ? (
                        <span className="px-2 py-0.5 text-xs rounded-md bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {row.images_ready || 0}/{row.images_total || 0}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                      <CreatedCell ms={row.createdAtMs} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => nav(`/tales/${row.id}`)} className="px-2 py-1 rounded-md bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-xs">Open</button>
                        <button onClick={() => rename(row.id)} className="px-2 py-1 rounded-md bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-xs">Rename</button>
                        <div className="hidden md:flex gap-2">
                          <button onClick={() => queueImages([row.id])} className="px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs">Queue</button>
                          <button onClick={() => packageNow([row.id])} className="px-2 py-1 rounded-md bg-green-600 hover:bg-green-500 text-white text-xs">Package</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">No tales found</td></tr>
                )}
                {loading && items.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* "Load more" for list */}
          {cursor !== null && (
            <div>
              <button 
                disabled={loading} 
                onClick={() => fetchPage(false)} 
                className="px-4 py-2 rounded-md bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Keep existing Kanban as secondary */}
      {tab === 'kanban' && <KanbanLegacy onRefresh={() => fetchPage(true)} />}
    </div>
  );
}

/** Render existing board component */
function KanbanLegacy({ onRefresh }: { onRefresh: () => void }) {
  return <KanbanBoard onRefresh={onRefresh} />;
}
